<template>
    <div class="mx-auto max-w-[1232px] px-3 py-4 sm:px-6 sm:py-8">
        <DataTableV2
            :title="'Team members (' + memberLength + ')'"
            :headers="headers"
            :items="members"
            :filters="filters"
            searchable
        >
            <template #icon>
                <Users class="size-6 text-new-neutral-800" />
            </template>

            <template #toolbar>
                <div class="flex items-center gap-2">
                    <Button
                        v-if="isOwner || isAdmin"
                        variant="default"
                        size="lg"
                        @click="inviteDialogOpen = true"
                    >
                        <UserPlus class="size-4" />
                        Invite member
                    </Button>

                    <Button
                        v-if="isOwner || isAdmin"
                        variant="outline"
                        size="lg"
                        as="a"
                        :href="route('invitations.index')"
                        @click.prevent="router.get(route('invitations.index'))"
                    >
                        Invitations
                    </Button>
                </div>
            </template>

            <template #item-profile_photo_path="{ data, row }">
                <div class="flex h-8 w-8 items-center justify-center">
                    <img
                        :src="data ? data : row.profile_photo_url"
                        width="32"
                        height="32"
                        class="size-8 shrink-0 rounded-none object-cover ring-1 ring-border"
                        alt=""
                    />
                </div>
            </template>

            <template #item-name="{ data }">
                <span class="text-sm font-medium text-foreground">{{ data }}</span>
            </template>

            <template #item-email="{ data }">
                <span class="text-sm text-muted-foreground">{{ data }}</span>
            </template>

            <template #item-created_at="{ data }">
                <span v-if="data" class="text-sm text-muted-foreground">
                    {{ format(new Date(data as string), 'MMM d, yyyy') }}
                </span>
            </template>

            <template #item-has_two_fa_enabled="{ data }">
                <div class="flex justify-left">
                    <Badge v-if="data" variant="success" class="gap-1 rounded-none">
                        <ShieldCheck class="size-3" />
                        Enabled
                    </Badge>
                    <Badge v-else variant="warning" class="gap-1 rounded-none">
                        <ShieldAlert class="size-3" />
                        Disabled
                    </Badge>
                </div>
            </template>

            <template #item-role="{ data }">
                <Badge variant="secondary" class="rounded-none">
                    {{ roleLabel(data as string) }}
                </Badge>
            </template>

            <template #item-actions="{ row }">
                <DropdownMenu v-if="(isOwner || isAdmin) && row.role !== 'owner'">
                    <DropdownMenuTrigger as-child>
                        <Button variant="ghost" size="icon" class="size-8">
                            <MoreVertical class="size-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem
                            v-for="role in roleOptions"
                            :key="role.value"
                            :class="{ 'font-semibold': role.value === row.role }"
                            @click="updateRole(row as IUser, role.value)"
                        >
                            <Check v-if="role.value === row.role" class="size-4" />
                            <div v-else class="size-4" />
                            {{ role.label }}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator v-if="isOwner" />
                        <DropdownMenuItem
                            v-if="isOwner"
                            variant="destructive"
                            @click="openRemoveDialog(row as IUser)"
                        >
                            <Trash2 class="size-4" />
                            Remove member
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </template>

            <template #empty>
                <div class="flex flex-col items-center justify-center py-8 gap-4">
                    <Users class="size-10 text-muted-foreground/50" />
                    <div class="flex flex-col items-center gap-1">
                        <p class="text-sm font-semibold text-foreground">No members found</p>
                        <p class="text-xs text-muted-foreground">
                            Invite team members to start collaborating.
                        </p>
                    </div>
                </div>
            </template>
        </DataTableV2>

        <InviteMemberDialog
            :open="inviteDialogOpen"
            :roles="inviteRoleOptions"
            :is-personal-team="teamProps.personal_team"
            @close="inviteDialogOpen = false"
        />

        <RemoveMemberDialog
            :open="removeDialogOpen"
            :member="selectedMember"
            :team="teamProps"
            :loading="removeLoading"
            @close="removeDialogOpen = false"
            @remove="removeMember"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { router, usePage } from '@inertiajs/vue3'
import { route } from 'ziggy-js'
import { format } from 'date-fns'
import { Users, UserPlus, ShieldCheck, ShieldAlert, MoreVertical, Trash2, Check } from 'lucide-vue-next'
import { toast } from 'vue-sonner'

import { DataTableV2 } from '@/components/ui/data-table-v2'
import type { IDataTableHeader, IPaginationData, IDataTableFilter } from '@/components/ui/data-table-v2'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

import DefaultLayoutV2 from '@/Layouts/DefaultV2.vue'
import usePermissions from '@/composables/usePermissions'
import useConfirm from '@/composables/useConfirm'
import IUser from '@/types/Members/IUser'
import ICurrentTeam from '@/types/Members/ICurrentTeam'
import { ITopolPageProps } from '@/types/TopolPageProps/TopolPageProps'

import InviteMemberDialog from './components/InviteMemberDialog.vue'
import RemoveMemberDialog from './components/RemoveMemberDialog.vue'

defineOptions({
    layout: DefaultLayoutV2,
})

const props = defineProps<{
    members: IPaginationData<IUser>
}>()

const { isAdmin, isOwner } = usePermissions()
const { show } = useConfirm()
const globalProps = computed(() => usePage<ITopolPageProps>().props)
const teamProps = computed(() => globalProps.value.team as ICurrentTeam)

const memberLength = computed(() => props.members.total_records)

const inviteDialogOpen = ref(false)
const removeDialogOpen = ref(false)
const selectedMember = ref<IUser>()
const removeLoading = ref(false)

const { getCurrentTeamMembersCount } = useTeamsAndMembers()

const hasNonOwnerMembers = computed(() =>
    props.members.data.some((m) => m.role !== 'owner')
)

const headers = computed<IDataTableHeader[]>(() => {
    const showActions = hasNonOwnerMembers.value && (isOwner.value || isAdmin.value)

    const cols: IDataTableHeader[] = [
        { text: '', id: 'profile_photo_path' },
        { text: 'Name', id: 'name', sortable: true, sortableId: 'name' },
        { text: 'Email', id: 'email', sortable: true, sortableId: 'email' },
        { text: 'Joined', id: 'created_at', sortable: true, sortableId: 'team_user.created_at' },
        { text: '2FA', id: 'has_two_fa_enabled', align: 'left', sortable: false },
        { text: 'Role', id: 'role', sortable: true, sortableId: 'role', align: showActions ? 'left' : 'right' },
    ]

    if (showActions) {
        cols.push({ text: '', id: 'actions', align: 'center' })
    }

    return cols
})

const filters: IDataTableFilter[] = [
    {
        column: 'role',
        label: 'Member role',
        values: [
            { label: 'Owners', value: 'owner' },
            { label: 'Admins', value: 'admin' },
            { label: 'Editors', value: 'editor' },
            { label: 'Admins', value: 'viewer' },
        ],
    },
]

const roleOptions = computed(() => {
    if (teamProps.value.personal_team) {
        return [{ label: 'Admin', value: 'viewer', description: 'Can perform all actions regarding Api Tokens.' }]
    }

    const roles = [
        { label: 'Administrator', value: 'admin', description: 'Can invite or remove team members and change member\u2019s rights. Can perform all actions with team templates.' },
        { label: 'Editor', value: 'editor', description: 'Can perform all actions with team templates. Can\u2019t invite or remove team members or change their rights.' },
        { label: 'Admin', value: 'viewer', description: 'Can only view team templates.' },
    ]

    if (isOwner.value) {
        roles.unshift({
            label: 'Team owner',
            value: 'owner',
            description: 'Only team owner can transfer team ownership to other team member.',
        })
    }

    return roles
})

const inviteRoleOptions = computed(() => {
    if (teamProps.value.personal_team) {
        return [{ label: 'Admin', value: 'viewer' }]
    }

    return [
        { label: 'Administrator', value: 'admin' },
        { label: 'Editor', value: 'editor' },
        { label: 'Admin', value: 'viewer' },
    ]
})

const roleLabel = (role: string) => {
    if (role === 'viewer') return 'Admin'
    return role.charAt(0).toUpperCase() + role.slice(1)
}

const updateRole = async (member: IUser, value: string) => {
    if (teamProps.value.personal_team) {
        return
    }

    if (value === 'owner') {
        const confirmed = await show({
            type: 'info',
            header: 'Transfer team ownership',
            message:
                "Are you sure you want to transfer team ownership to another team member? Team's billing address and payment details will be preserved for the new owner.",
        })

        if (!confirmed) {
            return
        }
    }

    router.put(
        route('members.update', { member: member.id }),
        { role: value },
        {
            preserveScroll: true,
            preserveState: false,
            onSuccess: () => {
                toast.success('Team member updated')
            },
            onError: () => {
                toast.error('There was an error updating your team member.')
            },
        }
    )
}

const openRemoveDialog = (member: IUser) => {
    selectedMember.value = member
    removeDialogOpen.value = true
}

const removeMember = () => {
    if (!selectedMember.value) return

    removeLoading.value = true
    router.delete(
        route('members.delete', { member: selectedMember.value.id }),
        {
            preserveScroll: true,
            onSuccess: () => {
                getCurrentTeamMembersCount()
                toast.success('Team member removed')
            },
            onError: () => {
                toast.error('There was an error removing your team member.')
            },
            onFinish: () => {
                removeDialogOpen.value = false
                removeLoading.value = false
            },
        }
    )
}
</script>
